'use client';

import React, { useEffect } from 'react';
import { CopyIcon, MessageCircleQuestion } from 'lucide-react';

import { copyToClipboard } from '@utils';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
  Label,
  Link,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SidebarMenuButton,
  SidebarMenuButtonProps,
  SidebarMenuItem,
  useToast,
} from '@design-system';
import { Path } from '@apply/server-actions/results/current-applicant.result';
import { useClientTranslations } from '@core-systems/i18n';
import { useCurrentApplicant } from '@apply/hooks/applicant/queries/use-current-applicant';
export function SupportLinkDynamic(): React.JSX.Element {
  const { t } = useClientTranslations('apply');
  const { toast } = useToast();
  const [selectedPath, setSelectedPath] = React.useState<Path | null>(null);
  const { applicant } = useCurrentApplicant();

  useEffect(() => {
    if (applicant) {
      setSelectedPath(applicant.activePaths[0]);
    }
  }, [applicant]);

  if (!applicant || !selectedPath) {
    return <SupportLinkTrigger disabled />;
  }
  const handleCopy = (): void => {
    copyToClipboard(selectedPath.campus.supportEmailAddress).then(() =>
      toast({
        variant: 'success',
        title: t('support.dialog.cta.copy.success.title'),
        duration: 2000,
      }),
    );
  };

  return (
    <SidebarMenuItem key="support">
      <Dialog>
        <DialogTrigger asChild>
          <SupportLinkTrigger disabled={applicant.activePaths.length < 1} />
        </DialogTrigger>
        <DialogContent>
          <DialogTitle>{t('support.dialog.title')}</DialogTitle>
          <DialogDescription />
          {applicant.activePaths.length > 1 && (
            <div className="gap-md flex flex-col">
              <p className="text-secondary">{t('support.dialog.form.instructions')}</p>
              <div className="flex flex-col sm:col-span-2 md:col-span-1">
                <Label className="mb-xs">{t('support.dialog.form.fields.path.label')} *</Label>
                <div className="max-w-full">
                  <Select
                    value={selectedPath.id}
                    onValueChange={(value) => setSelectedPath(applicant.activePaths.find((path) => path.id === value)!)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {applicant.activePaths.map((path) => (
                        <SelectItem value={path.id} key={path.id}>
                          {`${path.campus.name} - ${t(`path.types.${path.pathType}`)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <div className="gap-md flex flex-col">
            <p>{t('support.dialog.instructions')}</p>
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <Link
                className="w-full"
                href={`mailto:${selectedPath?.campus?.supportEmailAddress}?subject=[Apply#${applicant.id}] Issue with Apply`}
                isExternal
              >
                {selectedPath.campus.supportEmailAddress}
              </Link>
              <Button RightIcon={CopyIcon} srOnly="Copy" onClick={handleCopy} />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary" className="w-full">
                {t('support.dialog.cta.close')}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarMenuItem>
  );
}

export function SupportLinkTrigger(props: SidebarMenuButtonProps): React.JSX.Element {
  const { t } = useClientTranslations('apply');
  return (
    <SidebarMenuButton {...props}>
      <MessageCircleQuestion />
      {t('support.cta')}
    </SidebarMenuButton>
  );
}
